import React, { useState, useContext, useEffect } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import "../../Style/VATDeclarationPage.scss";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import FirstTable from "../../CRUD/Tax Reports/VATDeclaration/FirstTable";
import CompanyHeader from "../FinancialStatementCRUD/FinancialS_Components/CompanyHeader";

const MONTH_LABELS = {
    1:  "Janar",
    2:  "Shkurt",
    3:  "Mars",
    4:  "Prill",
    5:  "Maj",
    6:  "Qershor",
    7:  "Korrik",
    8:  "Gusht",
    9:  "Shtator",
    10: "Tetor",
    11: "Nëntor",
    12: "Dhjetor",
};

export default function VatDeclarationPage() {
    const { fiscalYear } = useContext(FiscalYearContext);
    const [month, setMonth] = useState(0);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [isGeneratingPrint, setIsGeneratingPrint] = useState(false);
    const [vatData, setVatData] = useState(null);
    const [companyInfo, setCompanyInfo] = useState(null);

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const headers = authHeader();
                const response = await axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", { headers });
                if (response.data && response.data.length > 0) {
                    setCompanyInfo(response.data[0]);
                }
            } catch (error) {
                console.error("Gabim ne marrjen e te dhenave te kompanise:", error);
            }
        };
        fetchCompany();
    }, []);

    const buildVatPdfDoc = () => {
        if (!vatData) return null;

        const { data, txf30, txf67, txf68, txf71, txf72 } = vatData;
        const d = (data && data.length > 0) ? data[0] : {};
        const n = (key) => Number(d[key] || 0);
        const fmt = (val) => Number(val || 0).toFixed(2);
        const monthLabel = MONTH_LABELS[month] || "Muaji";

        const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(companyInfo?.CompanyName || "Kompania", 105, 14, { align: "center" });
        doc.setFontSize(10);
        doc.text("Formulari i Deklarimit të TVSH-së", 105, 19, { align: "center" });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.text("VAT Declaration Form", 105, 23.5, { align: "center" });
        doc.setFontSize(8);
        doc.text(`Numri fiskal: ${companyInfo?.IdentificationNumber || "-"}`, 10, 28.5);
        doc.text(`Periudha: ${monthLabel} ${fiscalYear}`, 200, 28.5, { align: "right" });

        const sectionIndexes = new Set();
        const summaryIndexes = new Set();
        const bodyRows = [];
        let ri = 0;

        const addSection = (title) => {
            bodyRows.push([{ content: title, colSpan: 5 }]);
            sectionIndexes.add(ri++);
        };
        const addRow = (code, desc, base) => {
            bodyRows.push([code, desc, fmt(base), "—", "—"]);
            ri++;
        };
        const addPair = (baseCode, vatCode, desc, base, vat) => {
            bodyRows.push([baseCode, desc, fmt(base), vatCode, fmt(vat)]);
            ri++;
        };
        const addSummary = (code, desc, value) => {
            bodyRows.push([code, { content: desc, colSpan: 3 }, fmt(value)]);
            summaryIndexes.add(ri++);
        };
        const addCalc = (code, desc, value) => {
            bodyRows.push([code, desc, { content: fmt(value), colSpan: 3 }]);
            ri++;
        };

        // Section 1 — Furnizimet
        addSection("Furnizimet");
        addRow("[9]",  "Shitjet e liruara pa të drejtë kreditimi",  n("_9_Shitjet_Pa_Kreditim"));
        addRow("[10]", "Shitjet e liruara me të drejtë kreditimi",  n("_10_Shitjet_Liruara_Me_Kreditim"));
        addRow("[11]", "Eksportet",                                  n("_11_Eksportet"));
        addPair("[12]","[13]","Shitjet e tatueshme me normën 18%",                                         n("_12_Shitjet18"),         n("_12_Shitjet18") * 0.18);
        addPair("[14]","[15]","Shitjet e tatueshme me normën 8%",                                          n("_14_Shitjet8"),          n("_14_Shitjet8") * 0.08);
        addPair("[16]","[17]","Nota debitore e lëshuar, nota kreditore e pranuar me normë 18%",            n("_16_NotaDebitore18"),    n("_16_NotaDebitore18") * 0.18);
        addPair("[18]","[19]","Nota debitore e lëshuar, nota kreditore e pranuar me normë 8%",             n("_18_NotaDebitore8"),     n("_18_NotaDebitore8") * 0.08);
        addPair("[20]","[21]","Fatura e borxhit të keq e pranuar, inkasimi i B.K. me normë 18%",          n("_20_BorgjiKeq18"),       n("_20_BorgjiKeq18") * 0.18);
        addPair("[22]","[23]","Fatura e borxhit të keq e pranuar, inkasimi i B.K. me normë 8%",           n("_22_BorgjiKeq8"),        n("_22_BorgjiKeq8") * 0.08);
        addPair("[24]","[25]","Rregullimet për të rritur TVSH-në me normë 18%",                           n("_24_Rregullimet18"),     n("_24_Rregullimet18") * 0.18);
        addPair("[26]","[27]","Rregullimet / Ngarkesa e kundërt për Energjinë me normë 8%",               n("_26_Rregullimet8"),      n("_26_Rregullimet8") * 0.08);
        addPair("[28]","[29]","Blerjet që i nënshtrohen ngarkesës së kundërt",                            n("_28_NgarkesaKundert18"), n("_28_NgarkesaKundert18") * 0.18);
        addSummary("[30]", "Totali i TVSH-së së llogaritur", txf30);

        // Section 2 — Blerjet
        addSection("Blerjet");
        addRow("[31]", "Blerjet dhe importet pa TVSH",                                      n("_31_BlerjetPaTVSH"));
        addRow("[32]", "Blerjet dhe importet investive pa TVSH",                            n("_32_Blerjet_dhe_Imp_Inv_PaTVSH"));
        addRow("[33]", "Blerjet dhe importet me TVSH jo të zbritshme",                     n("_33_Blerjet_dhe_Imp_Inv_me_TVSH_jo_TeZbritshme"));
        addRow("[34]", "Blerjet dhe importet investive me TVSH jo të zbritshme",           n("_34_Blerjet_dhe_Imp_Inv_me_TVSH_jo_TeZbritshme"));
        addPair("[35]","[36]","Importet me normë 18%",                                      n("_35_Importet18"),          n("_35_Importet18") * 0.18);
        addPair("[37]","[38]","Importet me normë 8%",                                       n("_37_Importet8"),           n("_37_Importet8") * 0.08);
        addPair("[39]","[40]","Importet investive me normë 18%",                            n("_39_ImportetInvestive18"), n("_39_ImportetInvestive18") * 0.18);
        addPair("[41]","[42]","Importet investive me normë 8%",                             n("_41_ImportetInvestive8"),  n("_41_ImportetInvestive8") * 0.08);
        addPair("[43]","[44]","Blerjet vendore me normë 18%",                               n("_43_BlerjetVendore18"),    n("_43_BlerjetVendore18") * 0.18);
        addPair("[45]","[46]","Blerjet vendore me normë 8%",                                n("_45_BlerjetVendore8"),     n("_45_BlerjetVendore8") * 0.08);
        addPair("[47]","[48]","Blerjet investive vendore me normë 18%",                     n("_47_BlerjetInvestive18"),  n("_47_BlerjetInvestive18") * 0.18);
        addPair("[49]","[50]","Blerjet investive vendore me normë 8%",                      n("_49_BlerjetInvestive8"),   n("_49_BlerjetInvestive8") * 0.08);
        addPair("[51]","[52]","Blerjet nga fermerët vendorë me normë 8%",                   n("_51_BlerjetNgaFermeret8"), n("_51_BlerjetNgaFermeret8") * 0.08);
        addPair("[53]","[54]","Nota debitore e pranuar, nota kreditore e lëshuar me normë 18%", n("_53_NotaKreditore18"), n("_53_NotaKreditore18") * 0.18);
        addPair("[55]","[56]","Nota debitore e pranuar, nota kreditore e lëshuar me normë 8%",  n("_55_NotaKreditore8"),  n("_55_NotaKreditore8") * 0.08);
        addPair("[57]","[58]","Fatura e borxhit të keq e lëshuar, pagesa e B.K. me normë 18%", n("_57_BorgjiKeq18"),     n("_57_BorgjiKeq18") * 0.18);
        addPair("[59]","[60]","Fatura e borxhit të keq e lëshuar, pagesa e B.K. me normë 8%",  n("_59_BorgjiKeq8"),      n("_59_BorgjiKeq8") * 0.08);
        addPair("[61]","[62]","Rregullimet për të ulur TVSH-në me normë 18%",                   n("_61_Rregullimet18"),   n("_61_Rregullimet18") * 0.18);
        addPair("[63]","[64]","Rregullimet / Ngarkesa e kundërt për Energjinë me normë 8%",     n("_63_Rregullimet8"),    n("_63_Rregullimet8") * 0.08);
        addPair("[65]","[66]","E drejta e kreditimit të TVSH-së — ngarkesa e kundërt 18%",      n("_65_NgarkesaKundert18"), n("_65_NgarkesaKundert18") * 0.08);
        addSummary("[67]", "Totali i TVSH-së së zbritshme", txf67);

        // Section 3 — Kalkulimi
        addSection("Kalkulimi");
        addCalc("[68]", "Teprica e TVSH-së e bartur nga muaji i kaluar", txf68);
        addCalc("[69]", "Balanca kreditore e mbetur = (67 + 68 - 30) > 0", n("_65_NgarkesaKundert18"));
        addCalc("[70]", "Kërkesa për rimbursim", n("_65_NgarkesaKundert18"));
        addCalc("[71]", "Kreditimi i TVSH-së për muajin pasues = (69 - 70)", txf71);
        addCalc("[72]", "TVSH që duhet të paguhet = (30 - 67 - 68) > 0", txf72);

        autoTable(doc, {
            startY: 34,
            head: [["Kodi", "Përshkrimi", "Baza (€)", "Kodi TVSH", "TVSH (€)"]],  
            body: bodyRows,
            theme: "grid",
            styles: {
                fontSize: 7,
                cellPadding: 1,
                lineColor: [200, 200, 200],
                lineWidth: 0.1,
                textColor: [33, 33, 33],
            },
            headStyles: {
                fillColor: [40, 73, 69],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                halign: "center",
            },
            columnStyles: {
                0: { cellWidth: 16, halign: "center" },
                1: { cellWidth: 94 },
                2: { cellWidth: 32, halign: "right" },
                3: { cellWidth: 16, halign: "center", fillColor: [240, 246, 243], textColor: [59, 97, 82] },
                4: { cellWidth: 32, halign: "right" },
            },
            didParseCell: (hookData) => {
                if (sectionIndexes.has(hookData.row.index)) {
                    hookData.cell.styles.fillColor = [233, 241, 237];
                    hookData.cell.styles.fontStyle = "bold";
                    hookData.cell.styles.textColor = [31, 58, 54];
                }
                if (summaryIndexes.has(hookData.row.index)) {
                    hookData.cell.styles.fillColor = [245, 249, 247];
                    hookData.cell.styles.fontStyle = "bold";
                }
            },
            margin: { top: 10, right: 6, bottom: 10, left: 6 },
            pageBreak: "auto",
        });

        const totalPages = doc.internal.getNumberOfPages();
        for (let page = 1; page <= totalPages; page++) {
            doc.setPage(page);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(120, 120, 120);
            doc.text(`Faqe ${page}/${totalPages}`, 6, 292);
            doc.text("Gjeneruar nga Accountree", 200, 292, { align: "right" });
        }

        return { doc, monthLabel };
    };

    const handleDownloadPdf = async () => {
        if (isGeneratingPdf || isGeneratingPrint) return;

        if (month === 0) {
            window.alert("Zgjidhni muajin para se të gjeneroni PDF.");
            return;
        }

        if (!vatData) {
            window.alert("Të dhënat po ngarkohen. Provoni sërish.");
            return;
        }

        setIsGeneratingPdf(true);
        try {
            const result = buildVatPdfDoc();
            if (!result) {
                window.alert("Gabim gjatë ndërtimit të PDF.");
                return;
            }
            const { doc, monthLabel } = result;
            doc.save(`VAT_Declaration_${monthLabel}_${fiscalYear}.pdf`);
        } catch (error) {
            console.error("Gabim gjatë gjenerimit të PDF:", error);
            window.alert("Ndodhi gabim gjatë gjenerimit të PDF.");
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const handlePrintPdf = async () => {
        if (isGeneratingPdf || isGeneratingPrint) return;

        if (month === 0) {
            window.alert("Zgjidhni muajin para se të printoni.");
            return;
        }

        if (!vatData) {
            window.alert("Të dhënat po ngarkohen. Provoni sërish.");
            return;
        }

        setIsGeneratingPrint(true);
        try {
            const result = buildVatPdfDoc();
            if (!result) {
                window.alert("Gabim gjatë ndërtimit të PDF për printim.");
                return;
            }
            const { doc } = result;
            const blobUrl = doc.output("bloburl");

            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            iframe.src = blobUrl;
            document.body.appendChild(iframe);

            iframe.onload = () => {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
                setTimeout(() => {
                    document.body.removeChild(iframe);
                    URL.revokeObjectURL(blobUrl);
                }, 1000);
            };
        } catch (error) {
            console.error("Gabim gjatë printimit:", error);
            window.alert("Ndodhi gabim gjatë printimit.");
        } finally {
            setIsGeneratingPrint(false);
        }
    };

    return (
        <div className="VATD__Page">
            <div className="VATD__HeaderData">
                <CompanyHeader
                    reportTitle={`Formulari i Deklarimit të TVSH-së`}
                    reportSubtitle="VAT Declaration Form"
                    fiscalYear={fiscalYear}
                    onDownloadPdf={handleDownloadPdf}
                    isGeneratingPdf={isGeneratingPdf}
                    onPrint={handlePrintPdf}
                    isGeneratingPrint={isGeneratingPrint}
                    headerAction={
                        <div className="VATD__MonthSelect">
                            <label htmlFor="vatd-month">Muaji</label>
                            <select
                                id="vatd-month"
                                value={month}
                                onChange={(e) => setMonth(Number(e.target.value))}
                            >
                                <option value={0}>- Zgjidh muajin -</option>
                                {Object.entries(MONTH_LABELS).map(([id, name]) => (
                                    <option key={id} value={Number(id)}>{name}</option>
                                ))}
                            </select>
                        </div>
                    }
                />
            </div>

            {/* ── VAT table ─────────────────────────────────────────── */}
            {month !== 0 ? (
                <div className="VATD__TableSection">
                    <div className="VATD__TableSection__Head">
                        <h3>Deklarata e TVSH-së · {MONTH_LABELS[month]} {fiscalYear}</h3>
                    </div>
                    <FirstTable month={month} onDataChange={setVatData} />
                </div>
            ) : (
                <div className="VATD__TableSection">
                    <div className="VATD__EmptyState">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p>Zgjidh një muaj për të parë deklaratën e TVSH-së</p>
                        <span>Zgjidh muajin nga kontrollet më sipër</span>
                    </div>
                </div>
            )}
        </div>
    );
}